import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Container,
  Grid,
  Typography,
  Box,
  CircularProgress,
  Button,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Rating,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { useQuery } from 'react-query';
import { tmdbService } from '../services';
import { adService, useBannerAd, useSocialBarAd, useNativeBannerAd, useBottomNativeBanners } from '../services/adService';
import { Movie, Cast, Review } from '../types';
import Navbar from '../components/Navbar';
import SEO from '../components/SEO';

const MovieDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [showWatchDialog, setShowWatchDialog] = useState(false);
  const [showDownloadDialog, setShowDownloadDialog] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [downloadLink, setDownloadLink] = useState<string | null>(null);

  // Load ads
  useBannerAd('banner-ad-container');
  useSocialBarAd();
  useNativeBannerAd('native-banner-container');
  useBottomNativeBanners();

  // Handle popunder ads on component mount
  useEffect(() => {
    adService.loadPopunderAd();
  }, []);

  const { data: movie, isLoading: isLoadingMovie } = useQuery(
    ['movie', id],
    () => tmdbService.getMovieDetails(Number(id)),
    { enabled: !!id }
  );

  const { data: credits, isLoading: isLoadingCredits } = useQuery(
    ['credits', id],
    () => tmdbService.getMovieCredits(Number(id)),
    { enabled: !!id }
  );

  const { data: reviews, isLoading: isLoadingReviews } = useQuery(
    ['reviews', id],
    () => tmdbService.getMovieReviews(Number(id)),
    { enabled: !!id }
  );

  const handleWatchClick = async () => {
    if (!movie) return;
    
    try {
      // Show ads and redirect immediately
      window.location.href = `https://www.effectiveratecpm.com/kygiwwz2y?key=fb70cd46648c9121ff6523441ac5e943&movie=${encodeURIComponent(movie.title)}`;
    } catch (error) {
      console.error('Error in watch flow:', error);
    }
  };

  const handleDownloadClick = async () => {
    if (!movie) return;
    
    try {
      // Show ads and redirect immediately
      window.location.href = `https://www.effectiveratecpm.com/kygiwwz2y?key=fb70cd46648c9121ff6523441ac5e943&movie=${encodeURIComponent(movie.title)}`;
    } catch (error) {
      console.error('Error in download flow:', error);
    }
  };

  if (isLoadingMovie || isLoadingCredits || isLoadingReviews) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!movie) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h6">Movie not found</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#0a0a0a', color: 'white' }}>
      {movie && <SEO movie={movie} path={`/movie/${id}/${movie.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`} />}
      <Navbar />
      <Container maxWidth="xl" sx={{ pt: { xs: 8, md: 10 } }}>
        {/* Movie Header */}
        <Grid container spacing={4} sx={{ mb: 6 }}>
          <Grid item xs={12} md={4}>
            <Box sx={{ 
              position: 'relative',
              width: '100%',
              paddingTop: '150%',
              borderRadius: '8px',
              overflow: 'hidden'
            }}>
              <img
                src={tmdbService.getImageUrl(movie.poster_path, 'w500')}
                alt={movie.title}
                style={{ 
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={8}>
            <Typography variant="h3" gutterBottom sx={{ 
              fontSize: { xs: '2rem', md: '3rem' },
              lineHeight: 1.2
            }}>
              {movie.title}
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              gap: 1, 
              mb: 2,
              flexWrap: 'wrap'
            }}>
              <Chip label={movie.release_date.split('-')[0]} />
              <Chip label={`${movie.runtime} min`} />
              <Chip label={movie.vote_average.toFixed(1)} />
            </Box>
            <Typography variant="body1" paragraph sx={{ 
              fontSize: { xs: '0.9rem', md: '1rem' }
            }}>
              {movie.overview}
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              gap: 2, 
              mt: 4,
              flexDirection: { xs: 'column', sm: 'row' }
            }}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={handleWatchClick}
                fullWidth
                sx={{
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 'bold'
                }}
              >
                🎬 Watch Now
              </Button>
              <Button
                variant="outlined"
                color="primary"
                size="large"
                onClick={handleDownloadClick}
                fullWidth
                sx={{
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 'bold'
                }}
              >
                ⚡ Download
              </Button>
            </Box>
          </Grid>
        </Grid>

        {/* Native Banner Ad */}
        <Box sx={{ 
          my: 6, 
          display: 'flex', 
          justifyContent: 'center', 
          width: '100%',
          overflow: 'hidden'
        }}>
          <div id="container-8d6f163ab876c15b559a8542693a18d6"></div>
        </Box>

        {/* Cast Section */}
        <Typography variant="h4" gutterBottom sx={{ 
          mb: 4,
          fontSize: { xs: '1.5rem', md: '2rem' }
        }}>
          Cast
        </Typography>
        <Grid container spacing={2} sx={{ mb: 6 }}>
          {credits?.cast.slice(0, 6).map((cast: Cast) => (
            <Grid item xs={4} sm={3} md={2} key={cast.id}>
              <Paper sx={{ 
                p: 1, 
                textAlign: 'center',
                height: '100%'
              }}>
                <Box sx={{ 
                  position: 'relative',
                  width: '100%',
                  paddingTop: '150%',
                  borderRadius: '50%',
                  overflow: 'hidden',
                  mb: 1
                }}>
                  <img
                    src={tmdbService.getImageUrl(cast.profile_path, 'w185')}
                    alt={cast.name}
                    style={{ 
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                </Box>
                <Typography variant="subtitle2" sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>
                  {cast.name}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' } }}>
                  {cast.character}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* Reviews Section */}
        <Typography variant="h4" gutterBottom sx={{ 
          mb: 4,
          fontSize: { xs: '1.5rem', md: '2rem' }
        }}>
          Reviews
        </Typography>
        <List>
          {reviews?.results.slice(0, 5).map((review: Review) => (
            <ListItem key={review.id} divider>
              <ListItemAvatar>
                <Avatar 
                  src={tmdbService.getImageUrl(review.author_details.avatar_path, 'w92')}
                  sx={{ width: { xs: 40, sm: 56 }, height: { xs: 40, sm: 56 } }}
                />
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography component="span" variant="subtitle1" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                      {review.author}
                    </Typography>
                    <Rating value={review.author_details.rating || 0} readOnly size="small" />
                  </Box>
                }
                secondary={
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography component="span" variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>
                      {new Date(review.created_at).toLocaleDateString()}
                    </Typography>
                    <Box component="span" sx={{ 
                      mt: 1,
                      fontSize: { xs: '0.9rem', sm: '1rem' }
                    }}>
                      {review.content}
                    </Box>
                  </Box>
                }
              />
            </ListItem>
          ))}
        </List>

        {/* Bottom Native Banners */}
        <Box sx={{ mt: 6, mb: 6 }}>
          <Grid container spacing={3} justifyContent="center">
            <Grid item xs={12} md={6}>
              <div id="native-banner-bottom-1" style={{ width: '100%', height: '90px' }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <div id="native-banner-bottom-2" style={{ width: '100%', height: '90px' }} />
            </Grid>
          </Grid>
        </Box>
      </Container>
    </Box>
  );
};

export default MovieDetail; 